import { Component, effect, inject, input, model, output } from '@angular/core';
import { UtilsService } from '@dxc/data-access';
import { Tab } from '@shared/types';
import { HlmButtonDirective } from '@spartan-ng/button';
import { getTabVariant, tabClasses } from './tabs-base';

/**
 * Component responsible for displaying and managing tabs without route integration.
 *
 * @input tabs - The list of tabs to be displayed, each containing a label.
 * @input variant - The visual style of tabs ('page' or 'table').
 * @input level - Whether these are parent or child level tabs.
 * @model activeTab - A model representing the currently active tab.
 * @output activeTabChange - Emits when the active tab changes.
 *
 * This component supports in-place tab switching without route navigation.
 */
@Component({
  selector: 'dxc-static-tabs',
  standalone: true,
  imports: [HlmButtonDirective],
  template: `
    <!-- Container for navigation tabs with responsive design and custom scrollbar styling -->
    <nav role="tablist" aria-label="tabs" [class]="tabClasses[this.variant()]">
      <!-- Navigation tabs with horizontal scrolling -->
      <div [class]="tabClasses.navDiv">
        @for (tab of tabs(); track tab.label) {
          <button
            hlmBtn
            class="text-sm"
            role="tab"
            size="sm"
            [attr.aria-selected]="tab?.label === activeTab()?.label"
            [attr.tabindex]="tab?.label === activeTab()?.label ? 0 : -1"
            [variant]="getTabVariant(tab)"
            (click)="handleTabClick(tab)"
          >
            {{ utilsService.formatString(tab?.label || '') }}
          </button>
        }
      </div>
    </nav>
  `
})
export class StaticTabsComponent {
  public tabs = input.required<Tab[]>();
  public variant = input<'page' | 'table'>('page');
  public activeTab = model<Tab>();
  public level = input<'parent' | 'child'>('parent');
  public activeTabChange = output<Tab>();
  protected utilsService = inject(UtilsService);
  protected tabClasses = tabClasses;

  constructor() {
    // Effect hook to set the active tab to the first tab by default if none is set
    effect(() => {
      if (!this.activeTab() && this.tabs().length > 0) {
        this.activeTab.set(this.tabs()[0]);
      }
    });
  }

  /**
   * Determines the variant of the tab based on whether it is the active tab.
   *
   * @param tab - The tab to determine the variant for.
   * @returns hlmBtn variant.
   */
  protected getTabVariant(tab: Tab): 'outline' | 'link' | 'tab' | 'activeTab' {
    return getTabVariant(this.variant(), tab, this.activeTab());
  }

  /**
   * Handles tab click events.
   * Sets the active tab and emits the activeTabChange event.
   *
   * @param tab - The tab that was clicked.
   */
  protected handleTabClick(tab: Tab): void {
    this.activeTab.set(tab);
    this.activeTabChange.emit(tab);
  }
}
